import {Component, EventEmitter, Inject, Input, Optional, Output} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from "@angular/material/dialog";
import {TranslocoService} from "@ngneat/transloco";
import {Compbaser} from "../compbaser";
import {LocalStoreService} from "../../services";
import {take} from 'rxjs/operators';
import {StoreSelectorsService} from "../../services";
import {ITablesSelectorSet} from "../tables-selector/tables-selector.component";

@Component({
    selector: 'app-local-diag',
    styleUrls: ['./dialog-local.component.scss'],
    templateUrl: './dialog-local.component.html'
})
export class DialogLocalComponent extends Compbaser {

    localCodes;
    filteredLanguages;
    searchText = '';
    popupMode = false;
    currentLanguage = '';
    selectionOnlyMode = false; // New: selection-only mode (doesn't change app language)

    @Input() setLargeMode;
    @Input() setFontSize;
    @Input() iconButton = true;
    @Output() changed: EventEmitter<any> = new EventEmitter();

    constructor(@Optional() public dialogRef: MatDialogRef<DialogLocalComponent>,
                protected override dialog: MatDialog,
                private storeSelectorsService: StoreSelectorsService,
                private translocoService: TranslocoService,
                private localStoreService: LocalStoreService,
                @Optional() @Inject(MAT_DIALOG_DATA) data) {
        super();
        this.initLocals();
        this.filteredLanguages = [...this.localCodes];
        if (data) {
            this.popupMode = true;
            // Check if this is selection-only mode (for feed translation, not app language)
            if (data.selectionOnly) {
                this.selectionOnlyMode = true;
                this.currentLanguage = data.currentLanguage || 'en';
            }
        }
        if (!this.selectionOnlyMode) {
            this.translocoService.setDefaultLang('en');
            this.localSetup();
            this.currentLanguage = this.translocoService.getActiveLang();
        }
    }

    private transLocoExamples() {
        const avail = this.translocoService.getAvailableLangs();
        const lang = this.translocoService.getTranslation();
        const activeLocal = this.translocoService.getActiveLang();
        console.log('manual ' + this.translocoService.translate('navigation.scenes', {}, 'he'));
        this.cod(
            this.translocoService.selectTranslate('navigation.scenes')
                .subscribe(value => {
                    console.log('stream ' + value);
                })
        );
    }

    /** load language from localStore unless override exists in meta index.html in which case we use it **/
    private localSetup() {
        let local = this.localStoreService.getStorageValConfig('defaultLanguage');
        this.cod(
            this.storeSelectorsService.getLocalStatic().subscribe((v) => {
                if (v) local = v;
                this.setLocal(local);
            })
        );
    }

    private setLocal(local) {
        if (!local) return;
        this.translocoService.setActiveLang(local);
        this.localStoreService.setStorageValConfig('defaultLanguage', local);
        this.currentLanguage = local;
        this.changed.emit(local);
    }

    public onOpenDialog() {
        const dialogRefVal = this.dialog.open(DialogLocalComponent, {
            height: '700px',
            width: '500px',
            data: {selectionModel: ''},
            panelClass: 'modern-dialog'
        });
        this.cod(
            dialogRefVal.afterClosed().pipe(take(1)).subscribe((local) => {
                this.setLocal(local);
            })
        );
    }

    public selectLocal(local) {
        if (this.dialogRef) {
            // In selection-only mode, return both code and name
            if (this.selectionOnlyMode) {
                this.dialogRef.close({ code: local.code, name: local.local });
            } else {
                this.dialogRef.close(local.code);
            }
        } else {
            this.setLocal(local.code);
        }
    }

    public closeDialog() {
        if (this.dialogRef) {
            this.dialogRef.close();
        }
    }

    public filterLanguages() {
        const search = this.searchText.toLowerCase().trim();
        if (!search) {
            this.filteredLanguages = [...this.localCodes];
            return;
        }
        this.filteredLanguages = this.localCodes.filter(lang =>
            lang.local.toLowerCase().includes(search) ||
            lang.lang.toLowerCase().includes(search) ||
            lang.code.toLowerCase().includes(search)
        );
    }

    public clearSearch() {
        this.searchText = '';
        this.filterLanguages();
    }

    public isSelected(item): boolean {
        return item.code === this.currentLanguage;
    }

    private initLocals() {
        this.localCodes = [
            {local: 'English', lang: 'English', code: 'en'},
            {local: 'ChineseSimplified', lang: '汉语, 漢語, 华语, 華語', code: 'zh-CN'},
            {local: 'ChineseTraditional', lang: '古文', code: 'zh-TW'},
            {local: 'Hindi', lang: 'हिन्दी', code: 'hi'},
            {local: 'Spanish', lang: 'Español', code: 'es'},
            {local: 'Hebrew', lang: 'עברית', code: 'he'},
            {local: 'Arabic', lang: 'العربية', code: 'ar'},
            {local: 'Malay', lang: 'بهاس ملايو كلنتن', code: 'ms'},
            {local: 'Russian', lang: 'Русский', code: 'ru'},
            {local: 'Bengali', lang: 'বাংলা', code: 'bn'},
            {local: 'Portuguese', lang: 'Português', code: 'pt'},
            {local: 'French', lang: 'Français', code: 'fr'},
            {local: 'Afrikaans', lang: 'Afrikaans', code: 'af'},
            {local: 'Irish', lang: 'Gaeilge', code: 'ga'},
            {local: 'Albanian', lang: 'Shqip', code: 'sq'},
            {local: 'Italian', lang: 'Italiano', code: 'it'},
            {local: 'Japanese', lang: 'ウチナーヤマトゥグチ', code: 'ja'},
            {local: 'Azerbaijani', lang: 'Azərbaycanca, آذربایجان دیلی', code: 'az'},
            {local: 'Kannada', lang: 'ಕನ್ನಡ', code: 'kn'},
            {local: 'Basque', lang: 'Euskara', code: 'eu'},
            {local: 'Korean', lang: '조선말, 한국어', code: 'ko'},
            {local: 'Latin', lang: 'Latīna', code: 'la'},
            {local: 'Belarusian', lang: 'Беларуская', code: 'be'},
            {local: 'Latvian', lang: 'Latviešu', code: 'lv'},
            {local: 'Bulgarian', lang: 'български език', code: 'bg'},
            {local: 'Lithuanian', lang: 'Lietuvių', code: 'lt'},
            {local: 'Catalan', lang: 'Català', code: 'ca'},
            {local: 'Macedonian', lang: 'Mакедонски', code: 'mk'},
            {local: 'Maltese', lang: 'Malti', code: 'mt'},
            {local: 'Croatian', lang: 'Hrvatski', code: 'hr'},
            {local: 'Norwegian', lang: 'Norsk', code: 'no'},
            {local: 'Czech', lang: 'Český Jazyk', code: 'cs'},
            {local: 'Persian', lang: 'فارسی', code: 'fa'},
            {local: 'Danish', lang: 'Dansk', code: 'da'},
            {local: 'Polish', lang: 'Język', code: 'pl'},
            {local: 'Dutch', lang: 'Nederlands', code: 'nl'},
            {local: 'Romanian', lang: 'Română', code: 'ro'},
            {local: 'Esperanto', lang: 'Esperantaj', code: 'eo'},
            {local: 'Estonian', lang: 'Eesti', code: 'et'},
            {local: 'Serbian', lang: 'Српски', code: 'sr'},
            {local: 'Filipino', lang: 'Wikang Filipino', code: 'tl'},
            {local: 'Slovak', lang: 'Slovenčina', code: 'sk'},
            {local: 'Finnish', lang: 'Suomi', code: 'fi'},
            {local: 'Slovenian', lang: 'Slovenian', code: 'sl'},
            {local: 'Galician', lang: 'Galego', code: 'gl'},
            {local: 'Swahili', lang: 'Kiswahili', code: 'sw'},
            {local: 'Georgian', lang: 'ქართული', code: 'ka'},
            {local: 'Swedish', lang: 'Svenska', code: 'sv'},
            {local: 'German', lang: 'Deutsch', code: 'de'},
            {local: 'Tamil', lang: 'தமிழ்', code: 'ta'},
            {local: 'Greek', lang: 'Ελληνικά', code: 'el'},
            {local: 'Telugu', lang: 'తెలుగు', code: 'te'},
            {local: 'Gujarati', lang: 'ગુજરાતી', code: 'gu'},
            {local: 'Thai', lang: 'ภาษาไทย', code: 'th'},
            {local: 'Haitian', lang: 'Kreyòl Ayisye', code: 'ht'},
            {local: 'Turkish', lang: 'Türkçe', code: 'tr'},
            {local: 'Ukrainian', lang: 'Українська', code: 'uk'},
            {local: 'Urdu', lang: 'اُردُو', code: 'ur'},
            {local: 'Hungarian', lang: 'Magyar', code: 'hu'},
            {local: 'Vietnamese', lang: 'Tiếng Việt Nam', code: 'vi'},
            {local: 'Icelandic', lang: 'Íslenska', code: 'is'},
            {local: 'Welsh', lang: 'Cymraeg', code: 'cy'},
            {local: 'Indonesian', lang: 'Bahasa Indonesia', code: 'id'},
            {local: 'Yiddish', lang: 'ייִדיש', code: 'yi'}
        ];
    }

}
